// Demo prototype types for the new Discharge section. Mirrors the shape used by
// the Coordinate patient-list components but kept self-contained so the
// Discharge prototype can evolve independently.

import type { ClinicalReference } from '~/types/clinical-reference';

export type DischargeUrgency = 'routine' | 'urgent' | 'emergent';

export type DischargePhase = 'predict' | 'place' | 'refer' | 'monitor' | 'discharge' | 'followup';

export type DischargeDisposition =
  | 'home_health'
  | 'hospital_at_home'
  | 'snf_at_home'
  | 'hospice'
  | 'infusion_at_home'
  | 'palliative';

export type DischargeInsuranceType =
  | 'medicare'
  | 'medicare_advantage'
  | 'medicaid'
  | 'commercial';

export interface PrimaryContact {
  name: string;
  relationship: string;
  phone: string;
}

export interface DischargePatient {
  id: string;
  name: string;
  age: number;
  dob: string;
  mrn: string;
  facilityName: string;
  floor: number;
  room: number;
  disposition: DischargeDisposition; // currently selected disposition
  recommendedDisposition: DischargeDisposition; // original AI recommendation; never changes
  dispositionScore: number; // 0-100 confidence in the recommended disposition
  urgency: DischargeUrgency;
  phase: DischargePhase;
  insurance: string;
  insuranceType: DischargeInsuranceType;
  memberId: string;
  admitDate: string; // M/D/YYYY
  losTargetDays: number;
  currentLOS: number; // days since admission
  attendingPhysician: string;
  primaryDiagnosis: string;
  primaryContact: PrimaryContact;
  assignedTo: string | null; // null = unassigned
  // Home address string ("1234 Broadway, Nashville, TN 37203") and gender used
  // when a patient is scheduled from the Intake queue (mirrors PredictPatient).
  homeAddress?: string;
  gender?: string;
  // Set when the demo action ("referral accepted / order received") drops an
  // intake-eligible patient into the Intake queue; cleared once scheduled.
  sentToIntakeAt?: string; // ISO 8601
  scheduledAt?: string; // ISO 8601, set on "Schedule Visits" — removes from queue
  selectedProviderName?: string;
  declinedProviderName?: string; // most recent provider who declined a referral
  noResponseProviderName?: string; // most recent provider that did not respond
  referralSentAt?: string; // ISO 8601 timestamp when the referral was sent
  referralResponse?: 'awaiting' | 'no_response' | 'declined';
  monitorChecks?: string[]; // ids of completed monitor checklist items
  // Post-discharge follow-up gathered from the receiving agency (demo: collected
  // by an automated voice agent). Present once the patient reaches the followup
  // phase; refreshed each time an update is requested.
  agencyUpdate?: DischargeAgencyUpdate;
  // Optional explicit clinical detail carried over from a real source (e.g. a
  // Predict record). When set, the build* helpers use these instead of the
  // deterministic hash-based demo generation; any field omitted falls back to
  // the generated value. Lets real patients carry their true profile while
  // seeded demo patients stay auto-generated.
  programScores?: Partial<Record<DischargeDisposition, number>>;
  // Explicit per-disposition decision factors. When programScores is set, only
  // the scored dispositions are surfaced in the fit; any without explicit
  // factors here fall back to generated ones.
  decisionFactors?: Partial<Record<DischargeDisposition, DischargeDecisionFactor[]>>;
  ninetyDaySummary?: Partial<DischargeNinetyDaySummary>;
}

// Post-discharge status shared back by the receiving agency. In the demo this is
// framed as the result of an automated voice agent calling the agency.
export interface DischargeAgencyUpdate {
  seen: boolean; // whether the agency has completed a first home visit
  summary: string; // a few lines on how the patient is doing
  updatedAt: string; // ISO 8601 — when the automated agent last collected this
  revision: number; // which step of the demo progression this update reflects
}

// Color-graded chip styling for the disposition score, mirroring the Predict list.
export function getScoreStyles(score: number): { bgcolor: string; color: string } {
  if (score >= 80) return { bgcolor: '#E8F5E9', color: '#1B5E20' };
  if (score >= 60) return { bgcolor: '#FFF3E0', color: '#E65100' };
  return { bgcolor: '#FFEBEE', color: '#B71C1C' };
}

export const DISPOSITION_LABELS: Record<DischargeDisposition, string> = {
  home_health: 'Home Health',
  hospital_at_home: 'Hospital at Home',
  snf_at_home: 'SNF at Home',
  hospice: 'Hospice',
  infusion_at_home: 'Infusion at Home',
  palliative: 'Palliative',
};


export const PHASE_CHIP_STYLE: Record<DischargePhase, { label: string; bgcolor: string; color: string }> = {
  predict:   { label: 'Predict',   bgcolor: '#e3f2fd', color: '#1565c0' },
  place:     { label: 'Place',     bgcolor: '#e8eaf6', color: '#283593' },
  refer:     { label: 'Refer',     bgcolor: '#f3e8ff', color: '#6a1b9a' },
  monitor:   { label: 'Monitor',   bgcolor: '#e0f7f4', color: '#005a4e' },
  discharge: { label: 'Discharge', bgcolor: '#eceff1', color: '#37474f' },
  followup:  { label: 'Follow-Up', bgcolor: '#e8f5e9', color: '#1b5e20' },
};

export const PHASE_ORDER: DischargePhase[] = ['predict', 'place', 'refer', 'monitor', 'discharge', 'followup'];

export const URGENCY_CHIP_STYLE: Record<DischargeUrgency, { label: string; bgcolor: string; color: string }> = {
  routine:  { label: 'Routine',  bgcolor: '#eceff1', color: '#37474f' },
  urgent:   { label: 'Urgent',   bgcolor: '#fff3e0', color: '#bf360c' },
  emergent: { label: 'Emergent', bgcolor: '#ffebee', color: '#b71c1c' },
};

export const URGENCY_BORDER_COLOR: Record<DischargeUrgency, string> = {
  routine:  '#cfd8dc',
  urgent:   '#ffb74d',
  emergent: '#e57373',
};

export interface DischargeClinicalEvent {
  date: string; // ISO 8601
  type: 'admission' | 'discharge' | 'er_visit' | 'procedure' | 'medication_change' | 'pt_eval' | 'other';
  description: string;
  location?: string;
  // Where the patient went on a `discharge` event (e.g. 'Home', 'Home + Home
  // Health', 'SNF'). Drives the "Discharged To" stat in the 90-day summary.
  destination?: string;
}

export interface DischargeNinetyDaySummary {
  narrativeSummary: string;
  references?: ClinicalReference[];
  recentAdmissions: number;
  erVisits: number;
  totalInpatientDays: number;
  currentLOS: number;
  events: DischargeClinicalEvent[];
  trend: 'improving' | 'stable' | 'declining';
  trendDescription: string;
  keyFindings: string[];
  lastUpdated: string; // ISO 8601
}

export interface DischargeDecisionFactor {
  id: string;
  label: string;
  importance: number; // 0-100
  value: string;
  dataSource: string;
  timestamp: string; // ISO 8601
  contribution: 'positive' | 'negative';
}

export interface DischargeProgramFit {
  // Partial: a patient with an explicit fit surfaces only the dispositions its
  // real data covers; generated fits still populate all dispositions.
  scores: Partial<Record<DischargeDisposition, number>>;
  decisionFactors: Partial<Record<DischargeDisposition, DischargeDecisionFactor[]>>;
  recommended: DischargeDisposition;
}

export interface DischargeContextType {
  patients: DischargePatient[];
  getPatientById: (id: string) => DischargePatient | undefined;
  currentUser: string;
  takeOwnership: (patientId: string) => void;
  setPhase: (patientId: string, phase: DischargePhase) => void;
  setDisposition: (patientId: string, disposition: DischargeDisposition) => void;
  setSelectedProvider: (patientId: string, providerName: string | undefined) => void;
  setReferralSent: (patientId: string, sentAt: string | undefined) => void;
  setReferralResponse: (patientId: string, response: 'awaiting' | 'no_response' | 'declined' | undefined) => void;
  setDeclinedProvider: (patientId: string, providerName: string | undefined) => void;
  setNoResponseProvider: (patientId: string, providerName: string | undefined) => void;
  toggleMonitorCheck: (patientId: string, key: string) => void;
  // Advance a patient through discharge into the post-discharge follow-up phase,
  // seeding the first automated agency update (demo).
  sendToFollowUp: (patientId: string) => void;
  // Simulate re-running the automated agency call: refresh the agency update
  // with the next step of the demo progression and a new timestamp.
  requestAgencyUpdate: (patientId: string) => void;
  // Route an intake-eligible patient into the Intake queue (demo: referral
  // accepted / transfer order received).
  sendToIntake: (patientId: string) => void;
  // Mark a patient scheduled once a real patient/episode has been created from
  // the Intake queue, removing them from it.
  markScheduled: (patientId: string) => void;
}
